/* ============================================================
   Mighty slide-out cart — offer coordinator (gifts + discount codes)
   Runs a single SERIALIZED pipeline so gift-add and code-apply never race:
     1) ensure free gifts (when the trigger/90-day variant is in the cart)
     2) apply the right discount codes for the cart contents — ONE /cart/update.js
        write carrying `discount` (which REPLACES the cart's code list, so existing
        codes are re-sent alongside the new ones) and `sections`, whose response is
        both the settled cart and the rendered drawer. No /discount/ redirect endpoint,
        no read-back poll, no retry pass.
     3) render: normally ONE drawer re-render from server truth — but when the
        listicle's INSTANT pre-rendered drawer is showing (data-ml-static marker),
        verify server truth against it instead and only re-render on mismatch,
        so the shopper never sees a swap (no flicker).
   Triggered on load, on cartUpdate, and via window.mightyCartSettle() (the
   listicle ATC calls this after adding). Idempotent + re-entrant-safe.
   OWNERSHIP: this coordinator only knows Mighty's gifts and Mighty's codes, so it
   acts ONLY on carts that hold a Mighty product (or a gift) — see ownsCart(). Any
   other cart (Everbright, a future landing page with its own codes) is left alone:
   no code writes that could displace that page's codes, no extra re-renders.

   Config from the script tag: data-gift-variants, data-gift-trigger.
   Codes from <cart-drawer data-*>: sub-codes, gift-codes, onetime-codes, gift-variant.
   PER-PAGE CODES (2026-09-24, Laila: "the discounts should be specific to the page, and
   the offer"): a page can put its own codes on the line it adds, as line properties
   _mc_codes_sub (used while the line is a subscription) and _mc_codes_once (used while it
   is one-time). A line with those properties gets ITS codes; a line without them gets the
   drawer defaults above. The Mighty PDP uses this for MIGHTY25; every other page is unchanged.
   ============================================================ */
(function () {
  'use strict';
  const script = document.currentScript || document.querySelector('script[src*="mighty-cart-offers"]');
  const GIFTS = (script && script.dataset.giftVariants ? script.dataset.giftVariants : '')
    .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  const TRIGGER = script && script.dataset.giftTrigger ? String(script.dataset.giftTrigger).trim() : '';
  const attempted = new Set();

  function list(v) { return (v || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean); }
  function getCart() { return fetch(`${routes.cart_url}.js`).then(function (r) { return r.json(); }); }

  async function ensureGifts(cart) {
    if (!GIFTS.length || !TRIGGER) return false;
    // Shopper explicitly removed the gifts (safety valve) — don't re-add them.
    if (cart.attributes && cart.attributes._mc_gifts_off === 'true') return false;
    const ids = cart.items.map(function (i) { return String(i.variant_id); });
    if (ids.indexOf(TRIGGER) === -1) return false;
    const missing = GIFTS.filter(function (g) { return ids.indexOf(g) === -1; });
    if (!missing.length) return false;
    // /cart/update.js (NOT add.js): Shopify's web pixel auto-fires Meta AddToCart for every
    // item that goes through add.js, so gift-adds were tripling ATC on the kit (main + 2 gifts).
    // update.js inserts absent variants without that hook — the shopper's ONE real add (mlBuy's
    // add.js call) stays the only ATC. Trade-off: update.js can't set line properties, so gifts
    // no longer carry _gift — everything downstream identifies them by variant id instead
    // (cart-drawer.liquid gift_variant_ids, subscribe.js gift exclusion, staticMatches).
    const updates = {};
    missing.forEach(function (g) { updates[g] = 1; });
    await fetch('/cart/update.js', Object.assign({}, fetchConfig(), {
      body: JSON.stringify({ updates: updates }),
    }));
    return true;
  }

  // Which carts this coordinator owns: an item whose product handle starts with a
  // configured prefix (<cart-drawer data-owned-handle-prefixes="mighty">) or one of
  // the gift variants. Everything else is somebody else's offer — stay silent.
  function ownsCart(cart) {
    const cd = document.querySelector('cart-drawer');
    const prefixes = list(cd && cd.dataset.ownedHandlePrefixes).map(function (p) { return p.toLowerCase(); });
    return (cart.items || []).some(function (i) {
      if (GIFTS.indexOf(String(i.variant_id)) !== -1) return true;
      const h = String(i.handle || '').toLowerCase();
      return prefixes.some(function (p) { return h.indexOf(p) === 0; });
    });
  }

  // Returns the /cart/update.js response (settled cart + rendered `sections`) when a
  // code write happened, or null when nothing needed applying.
  async function applyCodes(cart) {
    const cd = document.querySelector('cart-drawer');
    if (!cd || !cart.items.length) return null;
    const giftVariant = (cd.dataset.giftVariant || '').trim();
    const subCodes = list(cd.dataset.subCodes);
    const giftCodes = list(cd.dataset.giftCodes);
    const oneCodes = list(cd.dataset.onetimeCodes);
    const hasGiftTrigger = giftVariant && cart.items.some(function (i) { return String(i.variant_id) === giftVariant; });
    // Additive: apply the codes for EVERY line present. Each line brings its page's own codes
    // (_mc_codes_sub / _mc_codes_once properties) or, without them, the drawer defaults for its
    // type (sub / one-time). A mixed cart gets the union; Shopify decides which lines each code
    // is eligible for.
    let want = [];
    const prefixes = list(cd.dataset.ownedHandlePrefixes).map(function (p) { return p.toLowerCase(); });
    cart.items.forEach(function (i) {
      if (GIFTS.indexOf(String(i.variant_id)) !== -1) return;
      // Only Mighty lines bring codes. An Everbright line in the same cart must not pull in
      // Mighty's default codes (it made MIGHTY50 outrank a PDP/quiz line's MIGHTY25, 2026-09-24).
      const h = String(i.handle || '').toLowerCase();
      if (!prefixes.some(function (p) { return h.indexOf(p) === 0; })) return;
      const isSub = !!i.selling_plan_allocation;
      const props = i.properties || {};
      const own = isSub ? props._mc_codes_sub : props._mc_codes_once;
      const codes = (own !== undefined && own !== null) ? list(own) : (isSub ? subCodes : oneCodes);
      codes.forEach(function (c) { if (want.indexOf(c) === -1) want.push(c); });
    });
    if (hasGiftTrigger) want = want.concat(giftCodes);
    if (!want.length) return null;
    const haveCodes = (cart.discount_codes || []).map(function (d) { return (d.code || '').toUpperCase(); }).filter(Boolean);
    const have = new Set(haveCodes.concat(
      (cart.cart_level_discount_applications || []).map(function (d) { return (d.title || '').toUpperCase(); })
    ));
    const toApply = want.filter(function (c) { return !have.has(c.toUpperCase()) && !attempted.has(c.toUpperCase()); });
    if (!toApply.length) return null;
    toApply.forEach(function (c) { attempted.add(c.toUpperCase()); });
    // ONE write. `discount` on /cart/update.js REPLACES the cart's whole code list
    // (verified on this store 2026-09-03), so the codes already on the cart go in the
    // same string as the new ones. Unknown or inapplicable codes come back flagged
    // (`applicable:false`), never as an error, so a shipping code that doesn't apply to
    // a subscription cart is harmless — exactly as it was through /discount/.
    const all = [];
    haveCodes.concat(toApply.map(function (c) { return c.toUpperCase(); })).forEach(function (c) { if (all.indexOf(c) === -1) all.push(c); });
    const res = await fetch('/cart/update.js', Object.assign({}, fetchConfig(), {
      body: JSON.stringify({ discount: all.join(','), sections: 'cart-drawer,cart-icon-bubble' }),
    }));
    if (!res.ok) throw new Error('update.js ' + res.status);
    return res.json();
  }

  function refreshBubble() {
    return fetch(`${routes.cart_url}?section_id=cart-icon-bubble`).then(function (r) { return r.text(); }).then(function (txt) {
      const bd = new DOMParser().parseFromString(txt, 'text/html');
      const nb = bd.getElementById('cart-icon-bubble');
      const cb = document.getElementById('cart-icon-bubble');
      if (nb && cb) cb.innerHTML = nb.innerHTML;
    }).catch(function () {});
  }

  function paintSections(sections) {
    const html = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
    const src = html.querySelector('.drawer__inner');
    const tgt = document.querySelector('#CartDrawer .drawer__inner');
    if (src && tgt) tgt.innerHTML = src.innerHTML;
    const sh = html.querySelector('cart-drawer');
    const lh = document.querySelector('cart-drawer');
    if (sh && lh) lh.classList.toggle('is-empty', sh.classList.contains('is-empty'));
    if (sections['cart-icon-bubble']) {
      const bd = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const nb = bd.getElementById('cart-icon-bubble');
      const cb = document.getElementById('cart-icon-bubble');
      if (nb && cb) cb.innerHTML = nb.innerHTML;
    }
  }

  // Paint from `sections` when the caller already holds a render (the update.js
  // response carries one); otherwise fetch a fresh section render.
  function refreshDrawer(sections) {
    if (sections && sections['cart-drawer']) {
      try { paintSections(sections); } catch (e) {}
      return Promise.resolve();
    }
    return Promise.all([
      fetch(`${routes.cart_url}?section_id=cart-drawer`).then(function (r) { return r.text(); }),
      refreshBubble(),
    ]).then(function (res) {
      const html = new DOMParser().parseFromString(res[0], 'text/html');
      const src = html.querySelector('.drawer__inner');
      const tgt = document.querySelector('#CartDrawer .drawer__inner');
      if (src && tgt) tgt.innerHTML = src.innerHTML;
      const sh = html.querySelector('cart-drawer');
      const lh = document.querySelector('cart-drawer');
      if (sh && lh) lh.classList.toggle('is-empty', sh.classList.contains('is-empty'));
    }).catch(function () {});
  }

  /* ---- static pre-render support (listicle instant drawer) ----
     mlBuy injects pre-rendered drawer markup carrying a hidden marker div:
       <div data-ml-static data-expect-variants="vid:qty,vid:qty" data-expect-total="14025" hidden></div>
     While that marker is present, settle() verifies the real cart against the
     expectation instead of re-rendering: on match it only copies the volatile
     cart identifiers (line index, line key) into the static markup so qty/remove
     keep working; on mismatch server truth wins and we re-render normally. */
  function staticMarker() { return document.querySelector('#CartDrawer [data-ml-static]'); }

  function staticMatches(cart, marker) {
    try {
      const expect = {};
      (marker.getAttribute('data-expect-variants') || '').split(',').forEach(function (p) {
        const kv = p.split(':'); if (kv[0]) expect[kv[0].trim()] = parseInt(kv[1] || '1', 10);
      });
      const got = {};
      cart.items.forEach(function (i) { const v = String(i.variant_id); got[v] = (got[v] || 0) + i.quantity; });
      const ev = Object.keys(expect);
      if (ev.length !== Object.keys(got).length) return false;
      for (let i = 0; i < ev.length; i++) { if (got[ev[i]] !== expect[ev[i]]) return false; }
      return parseInt(marker.getAttribute('data-expect-total') || '-1', 10) === cart.total_price;
    } catch (e) { return false; }
  }

  function syncStatic(cart) {
    const scope = document.querySelector('#CartDrawer .drawer__inner');
    if (!scope) return;
    cart.items.forEach(function (item, idx) {
      const line = idx + 1;
      const vid = String(item.variant_id);
      const input = scope.querySelector('.quantity__input[data-quantity-variant-id="' + vid + '"]');
      if (input) {
        input.setAttribute('data-index', line);
        input.id = 'Drawer-quantity-' + line;
        input.value = item.quantity;
      }
      const rm = scope.querySelector('cart-remove-button button[data-variant-id="' + vid + '"]');
      if (rm) {
        const host = rm.closest('cart-remove-button');
        host.setAttribute('data-index', line);
        host.id = 'CartDrawer-Remove-' + line;
      }
      const gift = scope.querySelector('.mc-gift-remove[data-variant-id="' + vid + '"]');
      if (gift) gift.setAttribute('data-key', item.key);
      const row = (input || gift) ? (input || gift).closest('.cart-item') : null;
      if (row) {
        row.id = 'CartDrawer-Item-' + line;
        const err = row.querySelector('.cart-item__error');
        if (err) err.id = 'CartDrawer-LineItemError-' + line;
      }
    });
  }

  async function settle(fresh, clearUpgrade) {
    try {
      let cart = await getCart();
      // The "You're subscribed & saving — smart move!" banner must ONLY follow an actual
      // OTP→subscription upgrade (which sets _mc_upgraded). It's cleared here on a direct
      // add (listicle ATC) or page load, so the flag can't persist and make the banner
      // show for every later subscription. The upgrade's own cartUpdate re-settle passes
      // clearUpgrade=falsy, so the banner survives the upgrade that earned it.
      if (clearUpgrade && cart.attributes && cart.attributes._mc_upgraded === 'true') {
        await fetch('/cart/update.js', Object.assign({}, fetchConfig(), { body: JSON.stringify({ attributes: { _mc_upgraded: '' } }) }));
        cart = await getCart();
      }
      // Clear a stale gift-decline flag on a fresh offer add (fresh=true), on page load, or once
      // the trigger has left the cart — so a one-time "remove gift" can never permanently suppress
      // the free gifts. Without this, the flag sticks on the cart and gifts stop auto-adding.
      const hasTrigger = TRIGGER && cart.items.some(function (i) { return String(i.variant_id) === TRIGGER; });
      if ((fresh || !hasTrigger) && cart.attributes && cart.attributes._mc_gifts_off === 'true') {
        await fetch('/cart/update.js', Object.assign({}, fetchConfig(), { body: JSON.stringify({ attributes: { _mc_gifts_off: '' } }) }));
        cart = await getCart();
      }
      if (!cart.items || !cart.items.length) return true;
      // Not a Mighty cart: no gifts, no codes, no re-render. Leave it to its own page.
      if (!ownsCart(cart)) return true;
      // Gifts + codes both derive from the current main items, so we can add gifts and apply
      // codes off the same cart snapshot (gift-add doesn't change sub/one-time/trigger state).
      await ensureGifts(cart);
      const applied = await applyCodes(cart);
      const sections = applied && applied.sections;
      const marker = staticMarker();
      if (marker) {
        const finalCart = applied || await getCart();
        if (staticMatches(finalCart, marker)) {
          syncStatic(finalCart);
          await refreshBubble();
        } else {
          // Server truth disagrees with the pre-render (pre-existing cart items, code didn't
          // stick, price drift) — replace the static content with reality. Rare by design.
          try { if (window.posthog && posthog.capture) posthog.capture('lp_drawer_static_mismatch', { page: '7-ways-mighty' }); } catch (e) {}
          await refreshDrawer(sections);
        }
      } else {
        await refreshDrawer(sections);
      }
      return true;
    } catch (e) {
      console.error('[mighty-cart] settle failed', e);
      return false;
    }
  }

  // Every settle entry point routes through ONE promise chain, so runs never overlap and each
  // caller awaits its OWN completed run. That matters for the listicle checkout gate: it awaits
  // mightyCartSettle()'s result, and an early/shared resolve would let a shopper through to
  // checkout before the discount codes are committed. (Replaces the old busy/queued flags,
  // which returned undefined to colliding callers.)
  let lastSettle = Promise.resolve(true);
  function queueSettle(fresh, clearUpgrade) {
    const run = function () { return settle(fresh, clearUpgrade); };
    lastSettle = lastSettle.then(run, run);
    return lastSettle;
  }

  // Page load re-enables gifts (fresh=true) and clears a stale upgrade flag (clearUpgrade=true)
  // so both self-heal from a prior session.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { queueSettle(true, true); });
  else queueSettle(true, true);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (ev) {
      if (ev && ev.source === 'mighty-offers') return;
      queueSettle();
    });
  }

  // The listicle ATC (mlBuy) calls this right after adding the offer. A direct add is a new
  // purchase intent (not an upgrade), so clear any stale _mc_upgraded flag (clearUpgrade=true).
  // Callers that just earned an upgrade (subscribe.js) pass clearUpgrade=false so the
  // "subscribed & saving" banner survives. Default stays true for backward compatibility.
  window.mightyCartSettle = function (fresh, clearUpgrade) { return queueSettle(fresh, clearUpgrade !== false); };

  // Gift removal (safety valve): mark gifts declined so ensureGifts won't re-add them on the
  // next settle, then drop the line and re-render. Delegated so it survives drawer re-renders.
  document.addEventListener('click', function (e) {
    const btn = e.target && e.target.closest ? e.target.closest('.mc-gift-remove') : null;
    if (!btn) return;
    e.preventDefault();
    const key = btn.getAttribute('data-key');
    if (!key) return;
    btn.disabled = true;
    fetch('/cart/update.js', Object.assign({}, fetchConfig(), { body: JSON.stringify({ attributes: { _mc_gifts_off: 'true' } }) }))
      .then(function () { return fetch('/cart/change.js', Object.assign({}, fetchConfig(), { body: JSON.stringify({ id: key, quantity: 0 }) })); })
      .then(function () { return refreshDrawer(); })
      .catch(function () { btn.disabled = false; });
  });
})();
/* build 2026-09-24b per-line page codes (_mc_codes_sub/_mc_codes_once); only Mighty lines bring codes */
